import {
  DEFAULT_LOAD_BALANCING_PREFERENCE,
  loadBalancingWeight,
  type LoadBalancingPreference,
} from "@afar-research/afar";
import type { EnvironmentId } from "@afar-research/contracts";
import { useEffect, useRef } from "react";

import { loadBalancing } from "../connection/loadBalancing";
import { useAtomCommand } from "../state/use-atom-command";
import { useEnvironments } from "../state/environments";
import { useClientSettings } from "./useSettings";

export interface AutoBalanceCandidate {
  readonly environmentId: EnvironmentId;
}

/**
 * Places a new thread's draft on a machine by itself, once.
 *
 * Once, deliberately. The composer decides when the draft first has somewhere to go and then
 * leaves it alone: a draft that moved between machines while the user was typing would change
 * what their branch, worktree, and provider mean underneath them. Choosing a machine by hand, or
 * choosing a branch or worktree, is the user taking the decision back, and it stays taken.
 */
export function useAutoBalancedDraftEnvironment(input: {
  readonly draftId: string | null;
  readonly locked: boolean;
  readonly environmentId: EnvironmentId | null;
  readonly candidates: ReadonlyArray<AutoBalanceCandidate>;
  readonly onChoose: (environmentId: EnvironmentId) => void;
}): void {
  const enabled = useClientSettings((settings) => settings.loadBalancingEnabled);
  const preferences = useClientSettings((settings) => settings.loadBalancingPreferences);
  const { environments } = useEnvironments();
  const chooseEnvironment = useAtomCommand(loadBalancing.chooseEnvironment, {
    // A placement that cannot be decided is an ordinary outcome the composer already handles by
    // leaving the draft where it is. Surfacing it as a failure would be noise, every time.
    reportFailure: false,
  });

  const placedDrafts = useRef(new Set<string>());
  const onChooseRef = useRef(input.onChoose);
  onChooseRef.current = input.onChoose;

  const { draftId, locked, candidates } = input;
  // Identity only: the array is rebuilt every render, and depending on it would re-run placement.
  const candidateKey = candidates.map((candidate) => candidate.environmentId).join(",");

  useEffect(() => {
    if (!enabled || locked || draftId === null || candidates.length < 2) return;
    if (placedDrafts.current.has(draftId)) return;
    // Claimed before the read so a re-render during it cannot start a second placement.
    placedDrafts.current.add(draftId);

    const ready = new Set(
      environments
        .filter((environment) => environment.connection.phase === "ready")
        .map((environment) => environment.environmentId),
    );
    const eligible = candidates
      .filter((candidate) => ready.has(candidate.environmentId))
      .map((candidate) => ({
        environmentId: candidate.environmentId,
        weight: loadBalancingWeight(
          (preferences[candidate.environmentId] as LoadBalancingPreference | undefined) ??
            DEFAULT_LOAD_BALANCING_PREFERENCE,
        ),
      }));
    if (eligible.length < 2) {
      // Nothing to weigh up. Leave the draft where the composer put it rather than moving it to
      // the single eligible machine, which may not be the one the project is open on.
      return;
    }

    let cancelled = false;
    void chooseEnvironment(eligible).then((result) => {
      if (cancelled || result._tag === "Failure") return;
      const chosen = result.value;
      if (chosen !== null && chosen !== input.environmentId) onChooseRef.current(chosen);
    });
    return () => {
      cancelled = true;
    };
    // `input.environmentId` is read at resolution time, not a trigger: a placement must not
    // re-run because the choice it just made changed the draft's environment.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, locked, draftId, candidateKey, preferences, environments, chooseEnvironment]);
}
